import type { TileRef, TilesetRef } from "./types.ts";

/**
 * The packed `u32` tile form used in serialised documents, compiled chunks and renderer hot paths (ADR-089):
 * bit 31 mirrors the tile horizontally, bit 30 vertically, bit 29 is kept for the quarter turn, bits 16 to 28
 * hold the tileset index and bits 0 to 15 the local tile id. The flag bits sit where Tiled keeps its gid
 * flags. This module is the only place that converts between it and `TileRef` or reads its bits.
 */

/**
 * Packed value of an empty cell.
 */
export const EMPTY_TILE = 0xffffffff;

/**
 * The bit that mirrors a tile horizontally.
 */
export const TILE_FLIP_X = 0x80000000;

/**
 * The bit that mirrors a tile vertically.
 */
export const TILE_FLIP_Y = 0x40000000;

/**
 * The bit kept for the quarter turn (Tiled's anti-diagonal flag); no reader accepts it yet.
 */
export const TILE_TURN = 0x20000000;

const ORIENTATION_MASK = 0xe0000000;
const INDEX_BITS = 0x1fff;
const TILE_SHIFT = 16;

/**
 * Largest tileset index that can be packed. Index `0x1fff` is reserved so no real tile equals `EMPTY_TILE`.
 */
export const MAX_TILESET_INDEX = 0x1ffe;

/**
 * Largest local tile ID that can be packed.
 */
export const MAX_TILE_ID = 0xffff;

/**
 * Refuses a tileset list longer than the packed tile form can index.
 *
 * @param count How many entries the list holds or would hold.
 * @param subject The list's description, read before the count, for example "the tileset list would hold".
 * @throws Error naming the count and the cap when `count` exceeds `MAX_TILESET_INDEX + 1`.
 */
export function assertTilesetListFits(count: number, subject: string): void {
  if (count > MAX_TILESET_INDEX + 1) {
    throw new Error(`${subject} ${count} entries; the packed tile form indexes at most ${MAX_TILESET_INDEX + 1}`);
  }
}

/**
 * A tile's mirroring.
 */
export interface TileFlips {
  readonly flipX: boolean;
  readonly flipY: boolean;
}

/**
 * Throws unless a value is an integer within an inclusive range.
 *
 * @param value Value to check.
 * @param max Inclusive upper bound.
 * @param label Name used in the error message.
 * @throws RangeError when the value is not an integer in `[0, max]`.
 */
function assertIndex(value: number, max: number, label: string): void {
  if (!Number.isInteger(value) || value < 0 || value > max) {
    throw new RangeError(`${label} must be an integer in [0, ${max}], received ${String(value)}`);
  }
}

/**
 * Packs a tileset index, local tile ID and mirroring into one unsigned 32-bit value.
 *
 * @param tilesetIndex Position of the tileset in `WorldDocument.tilesets`, at most `MAX_TILESET_INDEX`.
 * @param tile Local tile ID, at most `MAX_TILE_ID`.
 * @param flips The mirroring; none by default.
 * @returns The packed value.
 * @throws RangeError when either index is out of range.
 */
export function packTile(tilesetIndex: number, tile: number, flips?: { readonly flipX?: boolean; readonly flipY?: boolean }): number {
  assertIndex(tilesetIndex, MAX_TILESET_INDEX, "tilesetIndex");
  assertIndex(tile, MAX_TILE_ID, "tile");
  return ((flips?.flipX === true ? TILE_FLIP_X : 0) | (flips?.flipY === true ? TILE_FLIP_Y : 0) | (tilesetIndex << TILE_SHIFT) | tile) >>> 0;
}

/**
 * Unpacks a packed tile into its tileset index and local tile ID, whatever its mirroring.
 *
 * @param packed Packed value that is not `EMPTY_TILE`.
 * @returns The tileset index and tile ID.
 * @throws RangeError when the value is `EMPTY_TILE`, uses the reserved tileset index, carries a quarter turn,
 *   or is not an unsigned 32-bit integer.
 */
export function unpackTile(packed: number): { tilesetIndex: number; tile: number } {
  return { tilesetIndex: tilesetIndexOf(packed), tile: packed & MAX_TILE_ID };
}

/**
 * Reads a packed tile's tileset index, whatever its mirroring, without allocating: the hot read of the prop brush's
 * ground rules.
 *
 * @param packed Packed value that is not `EMPTY_TILE`.
 * @returns The tileset index.
 * @throws RangeError when the value is `EMPTY_TILE`, uses the reserved tileset index, carries a quarter turn,
 *   or is not an unsigned 32-bit integer.
 */
export function tilesetIndexOf(packed: number): number {
  assertIndex(packed, 0xffffffff, "packed");
  const tilesetIndex = (packed >>> TILE_SHIFT) & INDEX_BITS;
  if (tilesetIndex > MAX_TILESET_INDEX) {
    throw new RangeError(packed === EMPTY_TILE ? "packed value is EMPTY_TILE and has no tileset or tile" : `packed value uses the reserved tileset index ${tilesetIndex}`);
  }
  if ((packed & TILE_TURN) !== 0) {
    throw new RangeError(`packed value ${packed} carries a quarter turn, which this build does not read`);
  }
  return tilesetIndex;
}

/**
 * Reads a packed tile's local tile ID, whatever its mirroring, without allocating. Call it after `tilesetIndexOf`
 * has accepted the value.
 *
 * @param packed Packed value that is not `EMPTY_TILE`.
 * @returns The local tile ID.
 */
export function tileIdOf(packed: number): number {
  return packed & MAX_TILE_ID;
}

/**
 * Reads a packed tile's mirroring.
 *
 * @param packed Packed value; `EMPTY_TILE` reads as unmirrored.
 * @returns The mirroring.
 */
export function tileFlipsOf(packed: number): TileFlips {
  if (packed === EMPTY_TILE) {
    return { flipX: false, flipY: false };
  }
  return { flipX: (packed & TILE_FLIP_X) !== 0, flipY: (packed & TILE_FLIP_Y) !== 0 };
}

/**
 * Whether a packed value has an orientation bit set, a mirror or the quarter turn: what a document of a schema
 * before 6, whose tileset index could reach those bits, must never hold.
 *
 * @param packed Packed value.
 * @returns True when a bit from 29 to 31 is set on a non-empty cell.
 */
export function hasOrientationBits(packed: number): boolean {
  return packed !== EMPTY_TILE && (packed & ORIENTATION_MASK) !== 0;
}

/**
 * A packed tile without its mirroring, the key its frame is looked up by.
 *
 * @param packed Packed value.
 * @returns The value with bits 29 to 31 cleared; `EMPTY_TILE` unchanged.
 */
export function withoutTileFlips(packed: number): number {
  return packed === EMPTY_TILE ? packed : (packed & ~ORIENTATION_MASK) >>> 0;
}

/**
 * A packed tile moved to another tileset index, its tile and mirroring kept.
 *
 * @param packed Packed value.
 * @param tilesetIndex The new index, at most `MAX_TILESET_INDEX`.
 * @returns The new value; `EMPTY_TILE` unchanged.
 * @throws RangeError when the index is out of range.
 */
export function withTilesetIndex(packed: number, tilesetIndex: number): number {
  assertIndex(tilesetIndex, MAX_TILESET_INDEX, "tilesetIndex");
  if (packed === EMPTY_TILE) {
    return packed;
  }
  return ((packed & ORIENTATION_MASK) | (tilesetIndex << TILE_SHIFT) | (packed & MAX_TILE_ID)) >>> 0;
}

/**
 * A packed tile mirrored once more along an axis.
 *
 * @param packed Packed value.
 * @param axis `x` toggles the horizontal mirror, `y` the vertical.
 * @returns The new value; `EMPTY_TILE` unchanged.
 */
export function mirroredTile(packed: number, axis: "x" | "y"): number {
  return packed === EMPTY_TILE ? packed : (packed ^ (axis === "x" ? TILE_FLIP_X : TILE_FLIP_Y)) >>> 0;
}

/**
 * Converts a `TileRef` to the packed form using the world's tileset order.
 *
 * @param ref Tile reference, or null for an empty cell.
 * @param tilesets The world's tilesets in document order.
 * @returns The packed value, `EMPTY_TILE` for null.
 * @throws Error when the tileset id is not in `tilesets`, or RangeError when the tile ID is out of range.
 */
export function tileRefToPacked(ref: TileRef | null, tilesets: readonly TilesetRef[]): number {
  if (ref === null) {
    return EMPTY_TILE;
  }
  const index = tilesets.findIndex((tileset) => tileset.id === ref.tileset);
  if (index === -1) {
    throw new Error(`tileset ${ref.tileset} is not in the world`);
  }
  return packTile(index, ref.tile, ref);
}

/**
 * Converts a packed value back to a `TileRef` using the world's tileset order. A flag is written only when
 * it is set, so an unmirrored tile reads `{ tileset, tile }`.
 *
 * @param packed Packed value or `EMPTY_TILE`.
 * @param tilesets The world's tilesets in document order.
 * @returns The tile reference, or null for `EMPTY_TILE`.
 * @throws Error when the packed tileset index is beyond `tilesets`.
 */
export function packedToTileRef(packed: number, tilesets: readonly TilesetRef[]): TileRef | null {
  if (packed === EMPTY_TILE) {
    return null;
  }
  const { tilesetIndex, tile } = unpackTile(packed);
  const tileset = tilesets[tilesetIndex];
  if (tileset === undefined) {
    throw new Error(`packed tileset index ${tilesetIndex} is beyond the world's ${tilesets.length} tilesets`);
  }
  const { flipX, flipY } = tileFlipsOf(packed);
  return { tileset: tileset.id, tile, ...(flipX ? { flipX } : {}), ...(flipY ? { flipY } : {}) };
}

/**
 * Whether two tile references name the same tile with the same mirroring, an absent flag reading as false.
 *
 * @param a One reference, or null for an empty cell.
 * @param b The other.
 * @returns True when they are the same.
 */
export function sameTileRef(a: TileRef | null | undefined, b: TileRef | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) {
    return (a ?? null) === (b ?? null);
  }
  return a.tileset === b.tileset && a.tile === b.tile && (a.flipX === true) === (b.flipX === true) && (a.flipY === true) === (b.flipY === true);
}
